import { useState } from 'react';
import type { StatusView } from '@alfred/shared';
import { api, describeError } from './api.js';
import { GoogleSettings } from './GoogleSettings.js';
import { RoutinesSettings } from './RoutinesSettings.js';
import { SkillsSettings, UpgradesSettings } from './SkillsSettings.js';
import { ApiKeysSettings } from './ApiKeysSettings.js';
import { VideoFootageSettings } from './VideoFootageSettings.js';
import { LocationSettings } from './LocationSettings.js';
import { NotificationsSettings } from './NotificationsSettings.js';

export function Settings({
  status,
  onClose,
  onNewConversation,
  onChanged,
  onSignedOut,
}: {
  status: StatusView;
  onClose: () => void;
  onNewConversation: () => void;
  onChanged: () => Promise<void>;
  onSignedOut: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const guard = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const { spend } = status;
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <aside
        className="sheet"
        role="dialog"
        aria-label="Settings"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sheet-header">
          <h2>Settings</h2>
          <button className="icon-btn" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </header>

        <button className="btn btn-wide" onClick={onNewConversation}>
          New conversation
        </button>

        <section>
          <h3>Spending today</h3>
          <p className="muted">
            ${spend.spentUsd.toFixed(2)} of ${spend.capUsd.toFixed(2)} · resets{' '}
            {new Date(spend.resetsAt).toLocaleTimeString([], {
              hour: 'numeric',
              minute: '2-digit',
            })}
          </p>
        </section>

        <NotificationsSettings />
        <GoogleSettings />
        <RoutinesSettings />
        <SkillsSettings />
        <UpgradesSettings />
        <VideoFootageSettings />
        <ApiKeysSettings />
        <LocationSettings />

        <section>
          <h3>Passkeys</h3>
          <ul className="list">
            {status.passkeys.map((p) => (
              <li key={p.id}>
                <div>
                  <strong>{p.name}</strong>
                  <div className="muted small">
                    {p.lastUsedAt
                      ? `Last used ${new Date(p.lastUsedAt).toLocaleString()}`
                      : 'Not used yet'}
                  </div>
                </div>
                {status.passkeys.length > 1 && (
                  <button
                    className="btn btn-small"
                    disabled={busy}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Remove the passkey "${p.name}"? That device will be signed out.`,
                        )
                      ) {
                        void guard(() => api.removePasskey(p.id));
                      }
                    }}
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
          <button
            className="btn btn-wide"
            disabled={busy}
            onClick={() =>
              void guard(() =>
                api.register(window.prompt('Name for this passkey', 'Security key') ?? 'Passkey'),
              )
            }
          >
            Add a passkey on this device
          </button>
          <p className="muted small">
            For another device, open Alfred there and choose “Set it up with a code”. Codes come
            from <code>passkey new</code> on the server.
          </p>
        </section>

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <button
          className="btn btn-wide btn-danger-outline"
          onClick={() => void api.logout().finally(onSignedOut)}
        >
          Sign out
        </button>
      </aside>
    </div>
  );
}
